import { isEqualWith } from 'lodash';

/**
 * Sorts the keys of an object.
 *
 * @param object - The object to be sorted.
 * @returns A new object with sorted keys.
 */
export const sortObjectKeys = (object: Object): Object => {
  return Object.fromEntries(Object.entries(object).sort());
};

/**
 * Compares two objects by sorting their keys and then stringifying them.
 * All values are converted to strings before comparison to support for file uploads.
 *
 * @param obj1 - The first object to compare.
 * @param obj2 - The second object to compare.
 * @returns True if the sorted, stringified versions of the objects are equal, false otherwise.
 */
export const compareObjects = (obj1: Object, obj2: Object): boolean => {
  const stringifyValues = (obj: Object) => {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, String(value)]));
  };
  const sortedObj1 = sortObjectKeys(stringifyValues(obj1));
  const sortedObj2 = sortObjectKeys(stringifyValues(obj2));
  return JSON.stringify(sortedObj1) === JSON.stringify(sortedObj2);
};

/**
 * Filters out specified keys from an object.
 *
 * @param object - The object to filter.
 * @param keysToFilter - The keys to filter out from the object.
 * @returns A new object without the specified keys.
 */
export const filterObjectFields = <T extends Object>(object: T, keysToFilter: (keyof T)[]): Partial<T> => {
  const filteredEntries = Object.entries(object).filter(([key]) => !keysToFilter.includes(key as keyof T));
  return Object.fromEntries(filteredEntries) as Partial<T>;
};

/**
 * `lodash.isEqualWith` customizer that treats semantically-empty values as equivalent. Use
 * with `isEqualWith(a, b, looseEqualCustomizer)` — or via the convenience wrapper `looseIsEqual`.
 *
 * Handles two cases the default deep-equal trips on when comparing Formik / Yup form state:
 *   1. Scalar emptiness: `null`, `undefined`, and `""` are all treated as equal (Formik's
 *      `cloneDeep` on `initialValues` plus Yup casting can flip an optional string field
 *      between these representations without the admin touching it).
 *   2. Empty date-range pickers: an object of the shape
 *      `{ type: 'absolute', startDate: undefined, endDate: undefined }` (which Yup can
 *      materialize from a Cloudscape `DateRangePicker` schema) equals an unset value.
 *
 * Returning `undefined` falls back to lodash's default recursive equality.
 */
export const looseEqualCustomizer = (a: unknown, b: unknown): boolean | undefined => {
  const isEmptyScalar = (v: unknown) => v === undefined || v === null || v === '';
  if (isEmptyScalar(a) && isEmptyScalar(b)) return true;
  const isEmptyDateRange = (v: unknown): boolean =>
    v != null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    'startDate' in (v as Record<string, unknown>) &&
    'endDate' in (v as Record<string, unknown>) &&
    isEmptyScalar((v as Record<string, unknown>).startDate) &&
    isEmptyScalar((v as Record<string, unknown>).endDate);
  if ((isEmptyScalar(a) || isEmptyDateRange(a)) && (isEmptyScalar(b) || isEmptyDateRange(b))) return true;
  return undefined;
};

/**
 * Deep-equals two values while treating semantically-empty representations as equivalent.
 * See {@link looseEqualCustomizer} for the exact equivalence rules.
 */
export const looseIsEqual = (a: unknown, b: unknown): boolean => isEqualWith(a, b, looseEqualCustomizer);
